/**
 * simEventTenantFilter – pure tenant-isolation predicate for simulation SSE.
 *
 * Why this exists:
 *   The simulation SSE stream is a process-wide broadcast. BFF stamps each
 *   event with the engine's `tenantId` so FE consumers can reject events
 *   from a sim running for another tenant. Centralising the check in one
 *   pure function prevents drift between the live bridge, hydration paths,
 *   and any future subscriber.
 *
 * Rules:
 *   • If no tenant is selected in the FE, drop everything (caller should
 *     avoid connecting the stream in the first place; this is belt-and-
 *     braces).
 *   • If the event carries no `tenantId` (legacy payload before stamping
 *     was deployed), accept it — older servers would otherwise break.
 *   • If both sides carry a tenantId, they must match by string equality
 *     (numeric vs. string coercion resilience).
 */

export interface TenantTaggedEvent {
  tenantId?: number | string | null
  [k: string]: unknown
}

/**
 * @returns true when the event belongs to the currently-selected tenant
 *          and should be dispatched into the store; false otherwise.
 */
export function shouldProcessSimEvent(
  evt: TenantTaggedEvent | null | undefined,
  activeTenantId: number | string | null | undefined,
): boolean {
  if (evt == null) return false
  if (activeTenantId == null) return false
  const evtTid = evt.tenantId
  if (evtTid == null) return true // legacy unstamped event: allow
  return String(evtTid) === String(activeTenantId)
}
